<template>
  <div style="background-color: #eef3ea; min-height: 100vh;">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#FF6907" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>
    <div class="minister-dashboard__stats pa-3">
      <article
        v-for="(card, index) in cards"
        :key="index"
        class="stat-card stat-card--clickable"
        @click="redirectToPage(card.action, card.title)"
      >
        <div class="stat-card__head">
          <span class="stat-card__label">{{ card.title }}</span>
          <v-icon color="#2e5a38" size="22">{{ card.icon }}</v-icon>
        </div>
        <div class="stat-card__value">{{ formatNumber(card.value) }}</div>
        <div class="stat-card__action mt-3">
          <span>{{ card.action }}</span>
          <v-icon small color="#2e5a38" class="ml-1">mdi-arrow-right</v-icon>
        </div>
      </article>
    </div>

    <div class="minister-dashboard__grid pa-3">
      <!-- Left Column -->
      <div class="d-flex flex-column" style="gap: 16px;">
        <!-- Histogram Panel -->
        <section class="panel">
          <div class="panel__head">
            <div class="d-flex align-center justify-space-between wrap">
              <div class="panel__title-group">
                <div class="panel__title">
                  <v-icon color="#2e5a38" size="24" class="mr-2">mdi-chart-bar</v-icon>
                  Total Cases
                </div>
                <div class="panel__subtitle">Month-wise distribution of snake reports</div>
              </div>
              <div class="pt-2 pt-sm-0" style="width: 150px;">
                <v-select outlined dense :hide-details="true" :items="yearitems" v-model="lineyeardata"
                  class="chart-year-select"></v-select>
              </div>
            </div>
          </div>
          <div class="panel__chart mt-4">
            <div v-if="flag1">
              <highcharts :options="histogramOptions"></highcharts>
            </div>
            <div v-else>
              <v-skeleton-loader type="image" height="400" class="rounded-lg"></v-skeleton-loader>
            </div>
          </div>
        </section>

        <!-- Top Rescuers of the Year Panel -->
        <section class="panel">
          <div class="panel__head">
            <div class="d-flex align-center justify-space-between wrap">
              <div class="panel__title-group">
                <div class="panel__title">
                  <v-icon color="#2e5a38" size="24" class="mr-2">mdi-trophy-variant</v-icon>
                  Top Rescuers of the Year
                </div>
                <div class="panel__subtitle">Most active snake rescuers during {{ rescuerYear }}</div>
              </div>
              <div class="pt-2 pt-sm-0" style="width: 130px;">
                <v-select outlined dense :hide-details="true" :items="yearitems" v-model="rescuerYear"
                  class="chart-year-select" @change="fetchTopRescuersYear"></v-select>
              </div>
            </div>
          </div>

          <div class="mt-4">
            <div v-if="loadingRescuersYear" class="d-flex justify-center pa-5">
              <v-progress-circular indeterminate color="#2e5a38"></v-progress-circular>
            </div>
            <div v-else-if="topRescuersYear.length === 0" class="reporttwo mt-2 pl-2 text-center">
              No data available!
            </div>
            <div v-else class="rescuer-list">
              <div v-for="(item, index) in topRescuersYear" :key="item.id || index" class="rescuer-row">
                <div class="rescuer-row__rank" :class="'rank-' + (index + 1)">
                  <v-icon v-if="index === 0" color="#FFD700">mdi-crown</v-icon>
                  <span v-else>#{{ index + 1 }}</span>
                </div>
                <v-avatar size="40" class="rescuer-row__avatar mr-3" :color="getAvatarColor(index)">
                  <span class="white--text font-weight-bold text-uppercase">{{ getInitials(item.user ? item.user.name : '') }}</span>
                </v-avatar>
                <div class="rescuer-row__details">
                  <div class="rescuer-row__name">{{ item.user ? item.user.name : 'Unknown' }}</div>
                  <div class="rescuer-row__subtext">{{ getRescuerSummary(item) }}</div>
                </div>
                <div class="rescuer-row__badge" :class="'badge-' + (index + 1)">
                  <span class="font-weight-bold">{{ item.count }}</span>
                  <v-icon small color="white" class="ml-1">mdi-snake</v-icon>
                </div>
              </div>
            </div>
            <div v-if="topRescuersYear.length > 0" class="text-right mt-3">
              <span class="colorful-action-link" @click="redirectToUsersDetails()">
                View More <v-icon small color="#2e5a38">mdi-arrow-right</v-icon>
              </span>
            </div>
          </div>
        </section>

        <!-- Top Rescuers of the Month Panel -->
        <section class="panel">
          <div class="panel__head">
            <div class="d-flex align-center justify-space-between wrap">
              <div class="panel__title-group">
                <div class="panel__title">
                  <v-icon color="#2e5a38" size="24" class="mr-2">mdi-calendar-star</v-icon>
                  Top Rescuers of the Month
                </div>
                <div class="panel__subtitle">Most active snake rescuers in {{ getMonthName(rescuerMonth) }} {{ rescuerMonthYear }}</div>
              </div>
              <div class="d-flex align-center gap-2 pt-2 pt-sm-0">
                <div style="width: 110px;" class="mr-2">
                  <v-select outlined dense :hide-details="true" :items="monthsItems" v-model="rescuerMonth"
                    class="chart-year-select" @change="fetchTopRescuersMonth"></v-select>
                </div>
                <div style="width: 100px;">
                  <v-select outlined dense :hide-details="true" :items="yearitems" v-model="rescuerMonthYear"
                    class="chart-year-select" @change="fetchTopRescuersMonth"></v-select>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-4">
            <div v-if="loadingRescuersMonth" class="d-flex justify-center pa-5">
              <v-progress-circular indeterminate color="#2e5a38"></v-progress-circular>
            </div>
            <div v-else-if="topRescuersMonth.length === 0" class="reporttwo mt-2 pl-2 text-center">
              No data available!
            </div>
            <div v-else class="rescuer-list">
              <div v-for="(item, index) in topRescuersMonth" :key="item.id || index" class="rescuer-row">
                <div class="rescuer-row__rank" :class="'rank-' + (index + 1)">
                  <v-icon v-if="index === 0" color="#FFD700">mdi-crown</v-icon>
                  <span v-else>#{{ index + 1 }}</span>
                </div>
                <v-avatar size="40" class="rescuer-row__avatar mr-3" :color="getAvatarColor(index)">
                  <span class="white--text font-weight-bold text-uppercase">{{ getInitials(item.user ? item.user.name : '') }}</span>
                </v-avatar>
                <div class="rescuer-row__details">
                  <div class="rescuer-row__name">{{ item.user ? item.user.name : 'Unknown' }}</div>
                  <div class="rescuer-row__subtext">{{ getRescuerSummary(item) }}</div>
                </div>
                <div class="rescuer-row__badge" :class="'badge-' + (index + 1)">
                  <span class="font-weight-bold">{{ item.count }}</span>
                  <v-icon small color="white" class="ml-1">mdi-snake</v-icon>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- Right Column -->
      <div class="d-flex flex-column" style="gap: 16px;">
        <!-- District-wise Reports Map -->
        <section class="panel panel--map">
          <div class="panel__head">
            <div class="d-flex align-center justify-space-between wrap">
              <div class="panel__title-group">
                <div class="panel__title">
                  <v-icon color="#2e5a38" size="24" class="mr-2">mdi-map-marker-radius</v-icon>
                  District-wise Reports
                </div>
                <div class="panel__subtitle">Kerala district boundary view for {{ mapYear }}</div>
              </div>
              <div class="pt-2 pt-sm-0" style="width: 130px;">
                <v-select outlined dense :hide-details="true" :items="yearitems" v-model="mapYear"
                  class="chart-year-select"></v-select>
              </div>
            </div>
          </div>

          <div ref="districtMapPanel" class="district-map mt-4">
            <div v-if="districtMapLoading" class="district-map__loading">
              <v-progress-circular indeterminate color="#2e5a38" size="42" width="4"></v-progress-circular>
              <div class="district-map__loading-text">Loading Kerala district boundary map...</div>
            </div>
            <l-map
              v-else
              ref="districtMap"
              :zoom="mapZoom"
              :center="mapCenter"
              :options="{ zoomControl: true, attributionControl: false, dragging: true, doubleClickZoom: false, scrollWheelZoom: false, zoomSnap: 0.25, zoomDelta: 0.25 }"
              class="district-map__canvas"
              style="height: 100%; width: 100%;"
              @ready="handleDistrictMapReady"
            >
              <l-geo-json
                v-if="districtGeoJson"
                :key="mapKey"
                :geojson="districtGeoJson"
                :options-style="districtStyle"
              />

              <l-marker
                v-for="marker in districtMarkers"
                :key="marker.name"
                :lat-lng="marker.latlng"
                :icon="marker.icon"
              />
            </l-map>
            <div v-if="!districtMapLoading && !hasDistrictData" class="district-map__empty-state">
              Boundary view loaded. No district report counts available for {{ mapYear }} yet.
            </div>
          </div>
        </section>

        <!-- Pie Chart Panel -->
        <section class="panel">
          <div class="panel__head">
            <div class="panel__title">
              <v-icon color="#2e5a38" size="24" class="mr-2">mdi-snake</v-icon>
              Top 10 Common Snake Species {{ currentYear }}
            </div>
            <div class="panel__subtitle">Distribution of snake species reported during the year</div>
          </div>
          <div class="panel__chart d-flex align-center justify-center mt-4">
            <div v-if="flag" style="width: 100%;">
              <highcharts :options="highchartsOptions" :constructor-type="'chart'"></highcharts>
            </div>
            <div v-else style="width: 100%;">
              <v-skeleton-loader type="image" height="344" class="rounded-lg"></v-skeleton-loader>
            </div>
          </div>
          <div v-if="flag" class="text-center mt-4">
            <span class="colorful-action-link"
              style="color: #2e5a38 !important; background: rgba(46, 90, 56, 0.08) !important; border-color: rgba(46, 90, 56, 0.2) !important;"
              @click="redirectToSnakeDetails()">
              View Details <v-icon small color="#2e5a38">mdi-arrow-right</v-icon>
            </span>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import { LMap, LGeoJson, LMarker } from "vue2-leaflet";

const SNAKE_CHART_COLORS = [
  "#234c30",
  "#2e5a38",
  "#3d7c47",
  "#4f8c59",
  "#5f966d",
  "#70a57c",
  "#83b58f",
  "#97c5a3",
  "#abcfb7",
  "#c2decb",
];

const formatSnakeName = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const truncateSnakeName = (value = "", maxLength = 18) => {
  const formattedValue = formatSnakeName(value);
  if (formattedValue.length <= maxLength) {
    return formattedValue;
  }
  return `${formattedValue.slice(0, maxLength - 1).trimEnd()}…`;
};

const formatSnakeAxisLabel = (value = "") =>
  `<span title="${escapeHtml(formatSnakeName(value))}" style="display:inline-block;max-width:138px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;vertical-align:middle;">${escapeHtml(
    truncateSnakeName(value)
  )}</span>`;

const formatChartCount = (value) => Number(value || 0).toLocaleString("en-IN");

export default {
  components: {
    LMap,
    LGeoJson,
    LMarker,
  },
  data() {
    const currentDate = new Date();

    //new year wise graph
    const currentYear = new Date().getFullYear();
    const dynamicYears = [];
    for (let y = currentYear; y >= 2015; y--) {
      dynamicYears.push(y);
    }

    const currentMonth = currentDate.toLocaleString("default", {
      month: "long",
    });
    return {
      hoveredCard: null,
      currentYear,
      currentMonth,
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      pages: 0,
      count: 20,
      pendingRequests: 0,
      cards: [
        {
          title: "Total Users",
          value: 0,
          action: "View Map",
          icon: "mdi-account-circle",
          vert: require("@/assets/images/redvertical.png"),
          color: "green",
        },
        {
          title: "Snake Rescuers",
          value: 0,
          action: "View Map",
          icon: "mdi-shield-star",
          vert: require("@/assets/images/purplevert.png"),
          color: "blue",
        },
        {
          title: "Snakes Rescued",
          value: 0,
          action: "View Map",
          icon: "mdi-snake",
          vert: require("@/assets/images/yellowvert.png"),
          color: "red",
        },
        {
          title: "Identification Help",
          value: 0,
          action: "View Details",
          icon: "mdi-card-search",
          vert: require("@/assets/images/bluevert.png"),
          color: "yellow",
        },
        {
          title: "Snake Deaths",
          value: 0,
          action: "View Details",
          icon: "mdi-alert-octagon",
          vert: require("@/assets/images/greyvert.png"),
          color: "yellow",
        },
        {
          title: "Listed Hospitals",
          value: 0,
          action: "View Map",
          icon: "mdi-hospital-box",
          vert: require("@/assets/images/greenvert.png"),
          color: "red",
        },
        {
          title: "Public Users",
          value: 0,
          action: "View Details",
          icon: "mdi-account-multiple",
          vert: require("@/assets/images/orangevert.png"),
          color: "purple",
        },
        {
          title: "Listed Snakes",
          value: 0,
          action: "View Details",
          icon: "mdi-view-list",
          vert: require("@/assets/images/pinkvert.png"),
          color: "light-green",
        },
        {
          title: "Total Reports",
          value: 0,
          action: "View Details",
          icon: "mdi-file-document-multiple",
          vert: require("@/assets/images/bluevert.png"),
          color: "indigo",
        },
        {
          title: "Report Rescued",
          value: 0,
          action: "View Details",
          icon: "mdi-hand-heart",
          vert: require("@/assets/images/greenvert.png"),
          color: "teal",
        },
        {
          title: "Snakes Escaped",
          value: 0,
          action: "View Details",
          icon: "mdi-run",
          vert: require("@/assets/images/orangevert.png"),
          color: "orange",
        },
      ],
      dates: [],
      details: [
        { title: "Most Common Snakes - 2024", icon: "mdi-snake" },
        { title: "Top Rescuers of July", icon: "mdi-snake" },
        { title: "Top Rescuers of 2024", icon: "mdi-snake" },
      ],
      topsnakes: [],
      topusers: [],
      mapYear: currentYear,
      mapZoom: 9,
      mapCenter: [10.8505, 76.2711],
      districtGeoJson: null,
      districtChartMap: {},
      districtMapLoading: true,
      districtMapPendingRequests: 0,
      rescuerYear: currentYear,
      topRescuersYear: [],
      loadingRescuersYear: false,
      rescuerMonthYear: currentYear,
      rescuerMonth: currentDate.getMonth() + 1,
      topRescuersMonth: [],
      loadingRescuersMonth: false,
      districtMapResizeObserver: null,
      districtMapRefreshTimers: [],
      monthsItems: [
        { text: "January", value: 1 },
        { text: "February", value: 2 },
        { text: "March", value: 3 },
        { text: "April", value: 4 },
        { text: "May", value: 5 },
        { text: "June", value: 6 },
        { text: "July", value: 7 },
        { text: "August", value: 8 },
        { text: "September", value: 9 },
        { text: "October", value: 10 },
        { text: "November", value: 11 },
        { text: "December", value: 12 },
      ],
      chartOptions3: {
        chart: {
          height: 262,
          type: "bar",
        },
        plotOptions: {
          bar: {
            columnWidth: "45%",
            distributed: true,
          },
        },
        dataLabels: {
          enabled: true,
        },
        legend: {
          show: false,
        },
        // xaxis: {
        //   categories: [],
        //   labels: {
        //     style: {
        //       fontSize: "12px",
        //     },
        //   },
        //   title: {
        //     text: "Year",
        //   },
        // },

        //new year wise graph
        xAxis: {
          categories: [], // This will be populated with year data
          title: {
            text: "Month",
          },
        },

        yaxis: {
          title: {
            text: "Count",
          },
        },


      },
      series3: [
        {
          name: "Count",
          data: [],
        },
      ],
      // yearitems: ["Last 7 Years", "Last 5 Years"],
      // lineyeardata: "Last 5 Years",

      //new year wise graph
      yearitems: dynamicYears,
      lineyeardata: currentYear,

      options: {
        chart: {
          type: "donut",
        },
        labels: [], // For the animal names
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      series: [],
      flag: false,
      flag1: false,
      yearwiseData: [],
      histogramOptions: {
        chart: {
          type: "column", // Change to 'column' for histogram
          height: 400,
          backgroundColor: "transparent",
        },
        title: {
          text: "",
        },
        xAxis: {
          categories: [], // This will be populated with year data
          title: {
            text: "Month",
          },
        },
        credits: {
          enabled: false, // Disable the Highcharts watermark
        },
        yAxis: {
          min: 0,
          title: {
            text: "Total Cases",
          },
        },
        plotOptions: {
          column: {
            pointWidth: 30, // Set the width of the columns (adjust this value)
            dataLabels: {
              enabled: true, // Enable data labels if needed
              formatter: function () {
                return this.y.toLocaleString(); // Automatically adds commas based on locale
              },
            },
          },
        },
        series: [
          {
            name: "Reports",
            data: [], // This will be populated with values from yearData
            color: "#2e5a38",
          },
        ],
        exporting: {
          enabled: false, // Disable the export menu
        },
      },
      highchartsOptions: {
        chart: {
          type: "bar",
          height: 438,
          backgroundColor: "transparent",
          marginLeft: 168,
          marginRight: 72,
          spacingTop: 0,
          spacingRight: 12,
          spacingBottom: 0,
          spacingLeft: 0,
        },
        colors: SNAKE_CHART_COLORS,
        credits: {
          enabled: false,
        },
        title: {
          text: null,
        },
        legend: {
          enabled: false,
        },
        xAxis: {
          categories: [],
          title: {
            text: null,
          },
          lineColor: "#d8e4d2",
          tickLength: 0,
          labels: {
            useHTML: true,
            reserveSpace: true,
            style: {
              color: "#36533c",
              fontSize: "11px",
              fontWeight: "700",
            },
            formatter: function () {
              return formatSnakeAxisLabel(this.value);
            },
          },
        },
        yAxis: {
          min: 0,
          allowDecimals: false,
          title: {
            text: null,
            style: {
              color: "#6c7c6c",
              fontSize: "12px",
              fontWeight: "600",
            },
          },
          gridLineColor: "rgba(46, 90, 56, 0.1)",
          gridLineDashStyle: "ShortDot",
          labels: {
            style: {
              color: "#7c8a7b",
              fontSize: "11px",
            },
          },
        },
        tooltip: {
          useHTML: true,
          backgroundColor: "#ffffff",
          borderColor: "rgba(46, 90, 56, 0.18)",
          shadow: true,
          formatter: function () {
            return `
              <div style="font-size:13px;">
                <div style="font-weight:700;color:#173824;margin-bottom:4px;">${escapeHtml(
                  formatSnakeName(this.x)
                )}</div>
                <div style="color:#36533c;">Reports: <b>${formatChartCount(this.y)}</b></div>
              </div>
            `;
          },
        },
        accessibility: {
          point: {
            valueSuffix: " reports",
          },
        },
        exporting: {
          enabled: false,
        },
        plotOptions: {
          bar: {
            borderRadius: 8,
            pointWidth: 20,
            groupPadding: 0.14,
            pointPadding: 0.08,
            dataLabels: {
              enabled: true,
              inside: false,
              align: "left",
              x: 10,
              crop: false,
              overflow: "allow",
              formatter: function () {
                return formatChartCount(this.y);
              },
              style: {
                color: "#173824",
                fontSize: "11px",
                fontWeight: "700",
                textOutline: "none",
              },
            },
          },
        },
        series: [
          {
            name: "Reports",
            colorByPoint: true,
            data: [],
          },
        ],
        responsive: [
          {
            condition: {
              maxWidth: 768,
            },
            chartOptions: {
              chart: {
                height: 460,
                marginLeft: 126,
                marginRight: 42,
              },
              xAxis: {
                labels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
              plotOptions: {
                bar: {
                  pointWidth: 14,
                },
              },
            },
          },
        ],
      },

      seriesData: [],
    };
  },
  computed: {
    districtMarkers() {
      if (!this.districtGeoJson || !this.districtGeoJson.features) {
        return [];
      }
      const DISTRICT_OFFSETS = {
        alappuzha: [0.02, -0.20],
        ernakulam: [0.06, -0.06],
        kottayam: [0.04, 0.03],
        idukki: [0.08, 0.18],
        pathanamthitta: [-0.08, 0.08],
        kollam: [-0.08, -0.05],
      };
      return this.districtGeoJson.features
        .map((feature) => {
          const districtName = this.getDistrictName(feature);
          const lookupKey = districtName ? districtName.toLowerCase().trim() : "";
          const count = this.districtChartMap[lookupKey] || 0;
          if (!districtName || count === 0) {
            return null;
          }
          const bounds = L.geoJSON(feature).getBounds();
          if (!bounds.isValid()) {
            return null;
          }
          let latlng = bounds.getCenter();
          const key = districtName.toLowerCase().trim();
          if (DISTRICT_OFFSETS[key]) {
            latlng = L.latLng(
              latlng.lat + DISTRICT_OFFSETS[key][0],
              latlng.lng + DISTRICT_OFFSETS[key][1]
            );
          }
          const displayName = districtName.charAt(0).toUpperCase() + districtName.slice(1).toLowerCase();
          return {
            name: districtName,
            latlng,
            icon: L.divIcon({
              className: "district-count-icon",
              html: `
                <div class="district-marker-label">
                  <span class="district-marker-label__name">${displayName}</span>
                  <span class="district-marker-label__count">${count}</span>
                </div>
              `,
              iconAnchor: [0, 0],
            }),
          };
        })
        .filter(Boolean);
    },
    mapKey() {
      return this.mapYear + '_' + Object.keys(this.districtChartMap).length + '_' + Object.values(this.districtChartMap).join('_');
    },
    hasDistrictData() {
      return Object.values(this.districtChartMap).some((count) => Number(count) > 0);
    },
  },
  beforeMount() {
    this.pendingRequests = 3;
    this.appLoading = true;
    this.countData();
    this.yearData();
    this.topsnakesData();
  },
  mounted() {
    this.beginDistrictMapLoading(2);
    this.fetchGeoJson();
    this.loadMapData();
    this.fetchTopRescuersYear();
    this.fetchTopRescuersMonth();
    this.setupDistrictMapObserver();
    window.addEventListener("resize", this.handleWindowResize, { passive: true });
  },
  beforeDestroy() {
    this.clearDistrictMapRefreshTimers();
    if (this.districtMapResizeObserver) {
      this.districtMapResizeObserver.disconnect();
      this.districtMapResizeObserver = null;
    }
    window.removeEventListener("resize", this.handleWindowResize);
  },
  watch: {
    appLoading(isLoading) {
      if (!isLoading) {
        this.scheduleDistrictMapRefresh();
      }
    },
    lineyeardata() {
      this.yearData();
    },
    mapYear() {
      this.beginDistrictMapLoading(1);
      this.loadMapData();
    },
    districtGeoJson() {
      this.scheduleDistrictMapRefresh();
    },
  },
  methods: {
    beginDistrictMapLoading(requestCount = 1) {
      this.districtMapPendingRequests = requestCount;
      this.districtMapLoading = true;
    },
    finishDistrictMapLoading() {
      if (this.districtMapPendingRequests > 0) {
        this.districtMapPendingRequests -= 1;
      }
      if (this.districtMapPendingRequests <= 0) {
        this.districtMapPendingRequests = 0;
        this.districtMapLoading = false;
        this.scheduleDistrictMapRefresh();
      }
    },
    handleWindowResize() {
      this.scheduleDistrictMapRefresh(false);
    },
    handleDistrictMapReady() {
      this.scheduleDistrictMapRefresh();
    },
    clearDistrictMapRefreshTimers() {
      this.districtMapRefreshTimers.forEach((timerId) => clearTimeout(timerId));
      this.districtMapRefreshTimers = [];
    },
    setupDistrictMapObserver() {
      if (typeof ResizeObserver === "undefined" || !this.$refs.districtMapPanel) {
        return;
      }
      this.districtMapResizeObserver = new ResizeObserver(() => {
        this.scheduleDistrictMapRefresh(false);
      });
      this.districtMapResizeObserver.observe(this.$refs.districtMapPanel);
    },
    scheduleDistrictMapRefresh(shouldFit = true) {
      this.clearDistrictMapRefreshTimers();
      [0, 180, 600].forEach((delay) => {
        const timerId = setTimeout(() => {
          this.refreshDistrictMap(shouldFit);
        }, delay);
        this.districtMapRefreshTimers.push(timerId);
      });
    },
    refreshDistrictMap(shouldFit = true) {
      this.$nextTick(() => {
        if (!this.$refs.districtMap || !this.$refs.districtMap.mapObject) {
          return;
        }
        const map = this.$refs.districtMap.mapObject;
        map.invalidateSize();
        if (shouldFit) {
          this.fitDistrictMap();
        }
      });
    },
    onPointerEnter(event, index) {
      if (event.pointerType === "mouse") {
        this.hoveredCard = index;
      }
    },
    onPointerLeave(event) {
      if (event.pointerType === "mouse") {
        this.hoveredCard = null;
      }
    },
    redirectToSnakeDetails() {
      this.$router.push({ path: "/topcommonsnakes" });
    },
    // animalcountData() {
    //   this.appLoading = true;
    //   axios({
    //     method: "GET",
    //     url: "/hwc/animalsummary/reports",
    //     headers: {
    //       "x-auth-token": localStorage.getItem("token"),
    //     },
    //   })
    //     .then((response) => {
    //       if (response.data.status == true) {
    //         const animalReportSummary = response.data.data.animalReportSummary;
    //         this.series = animalReportSummary.map((item) => item.totalReports);
    //         this.options.labels = animalReportSummary.map((item) => item.name);
    //         this.totalconflictdata = response.data.data.totalReports;
    //         const seriesData = animalReportSummary.map((item) => ({
    //           name: item.name,
    //           y: item.totalReports,
    //         }));
    //         this.highchartsOptions.series[0].data = seriesData;
    //         this.flag = true;
    //         this.appLoading = false;
    //       } else {
    //         this.msg = response.data.msg;
    //         this.showSnackBar = true;
    //         this.appLoading = false;
    //       }
    //     })
    //     .catch((err) => {
    //       this.appLoading = false;
    //       this.ServerError = true;
    //       console.log(err);
    //     });
    // },
    topsnakesData() {
      axios({
        method: "GET",
        url: "/topsnakes/all",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          currentyear: this.currentYear,
        }
      })
        .then((response) => {
          if (response.data.status == true) {
            const animalReportSummary = Array.isArray(response.data.data.allSnakes)
              ? response.data.data.allSnakes
              : [];

            const topSnakeReports = animalReportSummary
              .filter((item) => item && item.snakeName)
              .map((item) => ({
                name: item.snakeName,
                count: Number(item.count || 0),
              }))
              .sort((first, second) => second.count - first.count)
              .slice(0, 10);

            const categories = topSnakeReports.map((item) => item.name);
            const seriesData = topSnakeReports.map((item, index) => ({
              name: item.name,
              y: item.count,
              color: SNAKE_CHART_COLORS[index % SNAKE_CHART_COLORS.length],
            }));

            this.$set(this.highchartsOptions.xAxis, "categories", categories);
            this.$set(this.highchartsOptions.series[0], "data", seriesData);
            this.flag = true;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.log(err);
        })
        .finally(() => {
          this.pendingRequests--;
          if (this.pendingRequests <= 0) this.appLoading = false;
        });
    },
    redirectToPage(action, title) {
      if (title === "Total Users") {
        this.$router.push({ path: "/usermap" });
      } else if (title === "Snake Rescuers") {
        this.$router.push({ path: "/rescuermap" });
      } else if (title === "Snakes Rescued") {
        this.$router.push({ path: "/snakerescued" });
      } else if (title === "Identification Help") {
        this.$router.push({ path: "/identificationhelp" });
      } else if (title === "Listed Hospitals") {
        this.$router.push({ path: "/hospitalMap" });
      } else if (title === "Snake Deaths") {
        this.$router.push({ path: "/snakebiteDeath" });
      } else if (title === "Listed Snakes") {
        this.$router.push({ path: "/listedSnakes" });
      } else if (title === "Public Users") {
        this.$router.push({ path: "/usersList" });
      } else if (title === "Total Reports") {
        this.$router.push({ path: "/snakereports" });
      } else if (title === "Report Rescued") {
        this.$router.push({ path: "/snakereports" });
      } else if (title === "Snakes Escaped") {
        this.$router.push({ path: "/snakereports" });
      }
    },

    redirectToUsersDetails() {
      this.$router.push({ path: "/topUsers" });
    },
    redirectToUsersyearDetails() {
      this.$router.push({ path: "/topUsersyear" });
    },
    countData() {
      axios({
        method: "GET",
        url: "/user/stats",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.cards[0].value = response.data.data.count_total_users;
            this.cards[1].value = response.data.data.count_total_rescuer;
            this.cards[2].value = response.data.data.count_total_locations;
            this.cards[3].value = response.data.data.count_identify;
            this.cards[4].value = response.data.data.count_management;
            this.cards[5].value = response.data.data.count_hospital;
            this.cards[6].value = response.data.data.count_users;
            this.cards[7].value = response.data.data.count_snakes;
            this.cards[8].value = response.data.data.count_total_reports;
            this.cards[9].value = response.data.data.count_report_rescued;
            this.cards[10].value = response.data.data.count_snake_escaped;
            this.topsnakes = response.data.data.topsnakes;
            this.topusers = response.data.data.topusers;
            // Note: flag (pie chart) is owned by topsnakesData — do not set it here
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.log(err);
        })
        .finally(() => {
          this.pendingRequests--;
          if (this.pendingRequests <= 0) this.appLoading = false;
        });
    },
    // yearData() {
    //   const yearMapping = {
    //     "Last 7 Years": 7,
    //     "Last 5 Years": 5,
    //   };
    //   const selectedYears = yearMapping[this.lineyeardata] || 5;
    //   // When called from the watcher (year filter change), reset flag1 and bump counter
    //   if (this.pendingRequests <= 0) {
    //     this.pendingRequests = 1;
    //     this.appLoading = true;
    //     this.flag1 = false;
    //   }
    //   axios({
    //     method: "GET",
    //     url: "/user/yearwise",
    //     headers: {
    //       "x-auth-token": localStorage.getItem("token"),
    //     },
    //     params: {
    //       year: selectedYears,
    //     },
    //   })
    //     .then((response) => {
    //       if (response.data.status == true) {
    //         this.yearwiseData = response.data.data;
    //         this.$set(this.histogramOptions.xAxis, 'categories', this.yearwiseData.map((item) => item.year));
    //         this.$set(this.histogramOptions.series[0], 'data', this.yearwiseData.map((item) => item.count));
    //         this.flag1 = true;
    //         this.dates = response.data.data;
    //       } else {
    //         this.msg = response.data.msg;
    //         this.showSnackBar = true;
    //       }
    //     })
    //     .catch((err) => {
    //       this.ServerError = true;
    //       console.log(err);
    //     })
    //     .finally(() => {
    //       this.pendingRequests--;
    //       if (this.pendingRequests <= 0) this.appLoading = false;
    //     });
    // },

    //new year wise graph
    yearData() {
      const selectedYear = this.lineyeardata;

      if (this.pendingRequests <= 0) {
        this.pendingRequests = 1;
        this.appLoading = true;
        this.flag1 = false;
      }

      axios({
        method: "GET",
        url: "/user/monthwise",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          year: selectedYear,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            const monthData = response.data.data;
            // Map months (Jan, Feb, Mar, etc.) to the X-axis
            this.$set(this.histogramOptions.xAxis, 'categories', monthData.map((item) => item.month));
            // Map the counts to the chart columns
            this.$set(this.histogramOptions.series[0], 'data', monthData.map((item) => item.count));
            this.flag1 = true;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.log(err);
        })
        .finally(() => {
          this.pendingRequests--;
          if (this.pendingRequests <= 0) this.appLoading = false;
        });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    districtStyle(feature) {
      const districtName = this.getDistrictName(feature);
      const lookupKey = districtName ? districtName.toLowerCase().trim() : "";
      const count = this.districtChartMap[lookupKey] || 0;
      if (count > 0) {
        return {
          color: "#000000",
          weight: 2,
          fillColor: "#3d7c47",
          fillOpacity: Math.min(0.92, 0.45 + count * 0.08),
        };
      } else {
        return {
          color: "#000000",
          weight: 1,
          fillColor: "#edf4ea",
          fillOpacity: 0.72,
        };
      }
    },
    getDistrictName(feature) {
      if (!feature || !feature.properties) {
        return "";
      }
      return (
        feature.properties.DISTRICT ||
        feature.properties.circleName ||
        feature.properties.name ||
        feature.properties.NAME ||
        feature.properties.district ||
        ""
      );
    },
    async fetchGeoJson() {
      try {
        const response = await axios({
          method: "GET",
          url: "/alldata/formap",
          headers: { "x-auth-token": localStorage.getItem("token") }
        });
        if (response.data.status === true) {
          this.districtGeoJson = response.data.data;
          this.scheduleDistrictMapRefresh();
        }
      } catch (error) {
        console.error("Error fetching Sarpa district map:", error);
      } finally {
        this.finishDistrictMapLoading();
      }
    },
    async loadMapData() {
      try {
        const response = await axios({
          method: "GET",
          url: "/user/districtwise",
          headers: { "x-auth-token": localStorage.getItem("token") },
          params: { year: this.mapYear }
        });
        if (response.data.status === true) {
          const districtMap = {};
          (response.data.data || []).forEach((item) => {
            if (item.name) {
              const lookupKey = item.name.toLowerCase().trim();
              districtMap[lookupKey] = item.count;
            }
          });
          this.districtChartMap = districtMap;
          this.scheduleDistrictMapRefresh();
        } else {
          this.districtChartMap = {};
        }
      } catch (error) {
        this.districtChartMap = {};
        console.error("Error loading Sarpa district counts:", error);
      } finally {
        this.finishDistrictMapLoading();
      }
    },
    fitDistrictMap() {
      this.$nextTick(() => {
        if (!this.$refs.districtMap || !this.$refs.districtMap.mapObject || !this.districtGeoJson) {
          return;
        }
        const map = this.$refs.districtMap.mapObject;
        const bounds = L.geoJSON(this.districtGeoJson).getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds.pad(0.02));
        }
      });
    },
    async fetchTopRescuersYear() {
      this.loadingRescuersYear = true;
      try {
        const response = await axios({
          method: "GET",
          url: "/user/toprescuers/year",
          headers: { "x-auth-token": localStorage.getItem("token") },
          params: { year: this.rescuerYear }
        });
        if (response.data.status === true) {
          this.topRescuersYear = response.data.data || [];
        }
      } catch (error) {
        console.error("Error fetching yearly top rescuers:", error);
      } finally {
        this.loadingRescuersYear = false;
      }
    },
    async fetchTopRescuersMonth() {
      this.loadingRescuersMonth = true;
      try {
        const response = await axios({
          method: "GET",
          url: "/user/toprescuers/month",
          headers: { "x-auth-token": localStorage.getItem("token") },
          params: { year: this.rescuerMonthYear, month: this.rescuerMonth }
        });
        if (response.data.status === true) {
          this.topRescuersMonth = response.data.data || [];
        }
      } catch (error) {
        console.error("Error fetching monthly top rescuers:", error);
      } finally {
        this.loadingRescuersMonth = false;
      }
    },
    getInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return parts[0][0].toUpperCase();
    },
    getAvatarColor(index) {
      const colors = ["#2e5a38", "#467852", "#5f966d", "#79b589", "#94d4a6"];
      return colors[index % colors.length];
    },
    getRescuerCode(item) {
      return item && item.rescuerProfile && item.rescuerProfile.rescuerCode
        ? item.rescuerProfile.rescuerCode
        : "N/A";
    },
    getRescuerDistrict(item) {
      return item && item.rescuerProfile && item.rescuerProfile.district
        ? item.rescuerProfile.district
        : "N/A";
    },
    getRescuerSummary(item) {
      return `${this.getRescuerCode(item)} • ${this.getRescuerDistrict(item)}`;
    },
    getMonthName(monthNum) {
      const item = this.monthsItems.find(m => m.value === monthNum);
      return item ? item.text : "";
    },
  },
};
</script>
<style scoped>
/* Stat Cards */
.minister-dashboard__stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.96) !important;
  border: 1px solid rgba(34, 64, 35, 0.08) !important;
  border-radius: 20px !important;
  box-shadow: 0 16px 28px rgba(18, 42, 24, 0.08) !important;
  padding: 16px 18px !important;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.stat-card--clickable:hover {
  transform: translateY(-4px);
  background: #ffffff !important;
  border-color: rgba(46, 90, 56, 0.3) !important;
  box-shadow: 0 20px 35px rgba(18, 42, 24, 0.12) !important;
}

.stat-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-card__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6c7c6c;
}

.stat-card__value {
  margin-top: 12px;
  font-size: 30px;
  line-height: 1;
  font-weight: 700;
  color: #173824;
}

.stat-card__action {
  font-size: 11px;
  font-weight: 600;
  color: #2e5a38;
  display: inline-flex;
  align-items: center;
}

/* Grid Layout */
.minister-dashboard__grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 16px;
}

.panel {
  background: rgba(255, 255, 255, 0.96) !important;
  border: 1px solid rgba(34, 64, 35, 0.08) !important;
  border-radius: 20px !important;
  box-shadow: 0 16px 28px rgba(18, 42, 24, 0.08) !important;
  padding: 24px !important;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
}

.panel--map {
  min-height: 590px;
}

.panel__head {
  margin-bottom: 8px;
}

.panel__title {
  font-size: 18px;
  font-weight: 700;
  color: #173824 !important;
  display: flex;
  align-items: center;
}

.panel__subtitle {
  font-size: 13px;
  color: #718171 !important;
  margin-top: 4px;
}

.panel__chart {
  flex: 1;
  min-height: 0;
}

/* Year Select - Modern Style */
.chart-year-select ::v-deep .v-input__slot {
  border-radius: 10px !important;
  background: #F8FAFC !important;
  border-color: rgba(46, 90, 56, 0.2) !important;
}

.chart-year-select.v-text-field--outlined.v-input--is-focused ::v-deep .v-input__slot {
  box-shadow: 0 0 0 3px rgba(46, 90, 56, 0.15) !important;
}

/* Rescuer Card Styles */
.rescuer-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  background: #f8fafc;
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-left: 4px solid #e2e8f0;
  cursor: pointer;
  height: 100%;
  min-height: 80px;
}

.rescuer-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.09);
  background: #ffffff;
}

.rescuer-item--1 {
  border-left-color: #FFD700;
}

.rescuer-item--1:hover {
  box-shadow: 0 10px 24px rgba(255, 215, 0, 0.2);
}

.rescuer-item--2 {
  border-left-color: #90CAF9;
}

.rescuer-item--2:hover {
  box-shadow: 0 10px 24px rgba(33, 150, 243, 0.15);
}

.rescuer-item--3 {
  border-left-color: #FFAB76;
}

.rescuer-item--3:hover {
  box-shadow: 0 10px 24px rgba(230, 81, 0, 0.15);
}

.rescuer-item--4,
.rescuer-item--5,
.rescuer-item--6 {
  border-left-color: #818CF8;
}

.rescuer-item--4:hover,
.rescuer-item--5:hover,
.rescuer-item--6:hover {
  box-shadow: 0 10px 24px rgba(99, 102, 241, 0.12);
}

.rescuer-rank {
  font-weight: 800;
  font-size: 15px;
  color: #94A3B8;
  margin-right: 14px;
  min-width: 28px;
  font-family: poppinsbold;
}

.rescuer-rank.rank-1 {
  color: #FFD700;
  font-size: 20px;
  text-shadow: 0 2px 8px rgba(255, 215, 0, 0.5);
}

.rescuer-rank.rank-2 {
  color: #90CAF9;
  font-size: 18px;
}

.rescuer-rank.rank-3 {
  color: #FFAB76;
  font-size: 16px;
}

.rescuer-info {
  flex-grow: 1;
  min-width: 0;
  overflow: hidden;
}

.rescuer-item .rescuername {
  font-family: poppinsmedium;
  font-size: 14px;
  color: #1E293B;
  font-weight: 600;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
  margin-bottom: 6px;
}

.rescuervalue-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: white !important;
  padding: 3px 12px;
  border-radius: 20px;
  font-family: poppinsbold;
  font-size: 13px;
  transition: all 0.3s ease;
}

.badge-rank-1 {
  background: linear-gradient(135deg, #FFD700, #FFA000);
  box-shadow: 0 3px 10px rgba(255, 215, 0, 0.4);
}

.badge-rank-2 {
  background: linear-gradient(135deg, #90CAF9, #1976D2);
  box-shadow: 0 3px 10px rgba(33, 150, 243, 0.3);
}

.badge-rank-3 {
  background: linear-gradient(135deg, #FFAB76, #E65100);
  box-shadow: 0 3px 10px rgba(230, 81, 0, 0.3);
}

.badge-rank-4,
.badge-rank-5,
.badge-rank-6 {
  background: linear-gradient(135deg, #818CF8, #4338CA);
  box-shadow: 0 3px 10px rgba(99, 102, 241, 0.3);
}

.rescuer-item:hover .rescuervalue-badge {
  transform: scale(1.06);
}

.colorful-action-link {
  font-size: 11px;
  cursor: pointer;
  font-family: poppinsmedium, sans-serif;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(46, 90, 56, 0.2) !important;
  color: #2e5a38 !important;
}

.colorful-action-link:hover {
  background: rgba(46, 90, 56, 0.1) !important;
  border-color: rgba(46, 90, 56, 0.3) !important;
}

.reporttwo {
  font-family: poppinsregular;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: #797979;
}

@media (max-width: 1100px) {
  .minister-dashboard__grid {
    grid-template-columns: 1fr;
  }

  .panel--map {
    min-height: 550px;
  }
}

/* Rescuer premium list styling */
.rescuer-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rescuer-row {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(34, 64, 35, 0.05);
  border-radius: 12px;
  padding: 10px 14px;
  transition: all 0.2s ease;
}

.rescuer-row:hover {
  transform: translateX(4px);
  background: #ffffff;
  border-color: rgba(46, 90, 56, 0.2);
  box-shadow: 0 4px 12px rgba(18, 42, 24, 0.06);
}

.rescuer-row__rank {
  font-weight: 800;
  font-size: 14px;
  color: #94a3b8;
  min-width: 30px;
  display: flex;
  justify-content: center;
}

.rescuer-row__rank.rank-1 {
  font-size: 18px;
}

.rescuer-row__avatar {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.rescuer-row__details {
  flex: 1;
  min-width: 0;
}

.rescuer-row__name {
  font-weight: 600;
  font-size: 14px;
  color: #173824;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rescuer-row__subtext {
  font-size: 11px;
  color: #718171;
  margin-top: 1px;
}

.rescuer-row__badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  color: #ffffff;
  background: #2e5a38;
}

.rescuer-row__badge.badge-1 {
  background: linear-gradient(135deg, #2e5a38, #1c3d25);
  box-shadow: 0 2px 6px rgba(46, 90, 56, 0.3);
}

.rescuer-row__badge.badge-2 {
  background: linear-gradient(135deg, #467852, #2e5a38);
  box-shadow: 0 2px 6px rgba(70, 120, 82, 0.2);
}

.rescuer-row__badge.badge-3 {
  background: linear-gradient(135deg, #5f966d, #467852);
}

.rescuer-row__badge.badge-4,
.rescuer-row__badge.badge-5,
.rescuer-row__badge.badge-6,
.rescuer-row__badge.badge-7,
.rescuer-row__badge.badge-8,
.rescuer-row__badge.badge-9,
.rescuer-row__badge.badge-10 {
  background: #8fae95;
  color: #173824;
}

/* Map Panel Styles */
.district-map {
  position: relative;
  display: block;
  width: 100%;
  height: 470px !important;
  min-height: 470px !important;
  flex: 0 0 470px;
  border-radius: 16px;
  overflow: hidden;
  background: radial-gradient(circle at top, #f6fbf3 0%, #e5efe0 58%, #dbe7d5 100%);
  border: 2px solid #000000;
  box-shadow: inset 0 0 0 1px rgba(61, 102, 56, 0.05), 0 4px 12px rgba(0, 0, 0, 0.08);
}

.district-map__canvas {
  width: 100%;
  height: 100% !important;
  min-height: 470px !important;
}

.district-map__loading {
  width: 100%;
  height: 100%;
  min-height: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #36533c;
  background: radial-gradient(circle at top, #f6fbf3 0%, #e5efe0 58%, #dbe7d5 100%);
}

.district-map__loading-text {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.district-map__empty-state {
  position: absolute;
  right: 14px;
  bottom: 14px;
  max-width: 280px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(96, 118, 98, 0.22);
  box-shadow: 0 10px 24px rgba(23, 56, 36, 0.1);
  color: #36533c;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  z-index: 500;
  pointer-events: none;
}

::v-deep .leaflet-container {
  background: #e8f4e8;
  width: 100% !important;
  height: 100% !important;
  min-height: 470px !important;
}

::v-deep .leaflet-control-zoom {
  border: 0 !important;
  box-shadow: 0 10px 24px rgba(23, 56, 36, 0.14) !important;
  margin: 14px !important;
}

::v-deep .leaflet-control-zoom a {
  width: 34px !important;
  height: 34px !important;
  line-height: 34px !important;
  background: #ffffff !important;
  color: #183824 !important;
  border: 1px solid #dce7d7 !important;
}

::v-deep .leaflet-control-zoom a:hover {
  background: #f4f8f1 !important;
  color: #102411 !important;
}

@media (max-width: 768px) {
  .panel--map {
    min-height: 490px;
  }

  .district-map {
    height: 360px !important;
    min-height: 360px !important;
    flex-basis: 360px;
  }

  .district-map__canvas {
    min-height: 360px !important;
  }

  ::v-deep .leaflet-container {
    min-height: 360px !important;
  }
}

::v-deep .district-count-icon {
  background: transparent;
  border: 0;
  white-space: nowrap;
}

::v-deep .district-marker-label {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(46, 90, 56, 0.22);
  border-radius: 20px;
  padding: 3px 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 5px 12px rgba(18, 42, 24, 0.12);
  white-space: nowrap;
  transform: translate(-50%, -50%);
  transition: all 0.2s ease;
}

::v-deep .district-marker-label:hover {
  transform: translate(-50%, -50%) scale(1.06);
  background: rgba(255, 255, 255, 0.98);
  border-color: rgba(46, 90, 56, 0.5);
  box-shadow: 0 8px 18px rgba(18, 42, 24, 0.18);
}

::v-deep .district-marker-label__name {
  font-size: 10px;
  font-weight: 600;
  color: #173824;
  text-transform: capitalize;
}

::v-deep .district-marker-label__count {
  background: #2e5a38;
  color: #ffffff;
  font-size: 9px;
  font-weight: 800;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
